import {
  WSActions,
  WSControl,
  WSElement,
  WSForm,
  WSList,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";
import { useMutationUpdateCustomerData } from "../../../../../query/onboarding/mutations/useMutationUpdateCustomerData";
import { ErrorContextKey } from "../../../../../services/platform";
import { useFlowContext } from "../../../../../shared/components/Flow";
import { emptyValuesUsPersonInfo } from "../../../components/FormPartialUsPersonInfo/emptyValues";
import { FormPartialUsPersonInfo } from "../../../components/FormPartialUsPersonInfo";
import { validationSchemaUsPersonInfo } from "../../../components/FormPartialUsPersonInfo/validationSchema";
import { OnboardingStep } from "../../../types";
import { FlowUsPersonInfoContext, FormDataUsPersonInfo } from "../getSteps";
import { format } from "date-fns";
import { useUserId } from "../../../../../query/hooks/helpers";
import { useQueryUserProfile } from "../../../../../query/users/queries";
import { useCallback, useMemo, useState } from "react";
import { verificationService } from "../../../../../shared/services/verificaiton";
import { useUsPersonMissingFields } from "../../../hooks/useUsPersonMissingFields";
import { PATH_SETTINGS_ACCOUNT_PERSONAL } from "../../../../Settings/paths";
import { useHistory } from "react-router-dom";
import { useModalAbandon } from "../useModalAbandon";
import { performVerification } from "../../../../../services/api/onboarding/verifications";
import { type onboardingApiSpec } from "@wingspanhq/wingspan-api-types";
import { getVerificationState } from "../../../steps/CreateAccount/useMutationSubmit";
import { useQueryUserProfileV2 } from "../../../../../query/user/queries/useQueryUserProfile";
import merge from "lodash/merge";
import { selectorUsPersonPartialFormData } from "../../../selectors/selectorUsPersonPartialFormData";

export const StepUsPersonInfoForm: React.FC<OnboardingStep> = props => {
  const [context, setContext] = useFlowContext<FlowUsPersonInfoContext>();
  const [updateCustomerData, updateMeta] = useMutationUpdateCustomerData();
  const history = useHistory();
  const modalAbandon = useModalAbandon();

  const userId = useUserId();
  const queryUser = useQueryUserProfile(userId);
  const queryUserProfile = useQueryUserProfileV2();

  const [loading, setLoading] = useState(false);

  const verificationLevel: onboardingApiSpec.ParameterVerificationLevel = "Tax";

  const usPersonMissingFields = useUsPersonMissingFields(verificationLevel);

  const isUsPersonMismatch = useMemo(
    () => verificationService.getIsUsPersonMismatch(usPersonMissingFields),
    [usPersonMissingFields]
  );

  const goBack = useCallback(() => {
    history.push(PATH_SETTINGS_ACCOUNT_PERSONAL);
  }, [history]);

  return (
    <WSPanel shimmer={queryUser.isLoading || queryUserProfile.isLoading}>
      <WSForm<FormDataUsPersonInfo>
        defaultValues={
          context.usPersonInfo ?? {
            isUsPerson: false,
            usPersonInfo: merge(
              emptyValuesUsPersonInfo,
              selectorUsPersonPartialFormData({
                dob: queryUser.data?.profile.dob,
                userProfile: queryUserProfile.data
              })
            )
          }
        }
        validationSchema={Yup.object().shape({
          isUsPerson: Yup.boolean(),
          usPersonInfo: Yup.mixed().when("isUsPerson", {
            is: true,
            then: validationSchemaUsPersonInfo
          })
        })}
        onSubmit={async data => {
          setContext(prev => ({ ...prev, usPersonInfo: data }));
          setLoading(true);

          if (data.isUsPerson) {
            await updateCustomerData(
              {
                relationshipType: "Entity",
                requestData: {
                  customerData: {
                    taxProfiles: {
                      US: {
                        firstName: data.usPersonInfo.firstName || undefined,
                        lastName: data.usPersonInfo.lastName || undefined,
                        middleName: data.usPersonInfo.middleName || undefined,
                        individualTaxId: data.usPersonInfo?.ssn,
                        startDate:
                          data.usPersonInfo?.usPersonSince === "date" &&
                          data.usPersonInfo?.usPersonDate
                            ? data.usPersonInfo?.usPersonDate
                            : queryUser.data?.profile.dob
                              ? format(
                                  queryUser.data?.profile.dob,
                                  "yyyy-MM-dd"
                                )
                              : undefined
                      }
                    }
                  }
                }
              },
              {
                async onSuccess() {
                  // TODO: we should try to find smarter way to fix this case :D
                  try {
                    // 5. Perform verification
                    await performVerification(verificationLevel);
                  } catch (error: any) {
                    if (
                      error?.response?.data?.error ===
                      "Cannot perform verification at this level"
                    ) {
                      console.error("Verification error skipped", error);
                    } else {
                      setLoading(false);
                      throw error;
                    }
                  }

                  const { usPersonStatus, missingData } =
                    await getVerificationState(verificationLevel);

                  const shouldStop =
                    usPersonStatus === "UpdateRequired" &&
                    missingData.profiles?.["US"]?.requiredFields &&
                    missingData.profiles?.["US"]?.requiredFields?.length > 0;

                  setLoading(false);

                  if (!shouldStop) props.onNext?.();
                },
                onError() {
                  setLoading(false);
                }
              }
            );
          } else {
            goBack();
          }
        }}
      >
        <WSList gap="2XL">
          <WSElement>
            <WSSectionToolbar title="Update taxpayer information" />
            <WSText.ParagraphSm mt="L" color="gray500">
              Please review to ensure the information is accurate before
              submitting
            </WSText.ParagraphSm>
          </WSElement>

          <WSElement>
            <WSText.ParagraphSm weight="medium">
              Add U.S. person information
            </WSText.ParagraphSm>
            <WSText.ParagraphSm mt="XS" color="gray500">
              Only check the box to confirm you&apos;re a U.S. citizen, green
              card holder, or U.S. tax resident—even if you live in another
              country.
            </WSText.ParagraphSm>
            <WSForm.Field
              mt="L"
              name="isUsPerson"
              component={WSControl}
              componentProps={{
                type: "checkbox",
                size: "S",
                label: "Yes, I am a U.S. person"
              }}
            />
          </WSElement>

          <WSForm.Value name="isUsPerson">
            {(isUsPerson: boolean) =>
              isUsPerson ? (
                <FormPartialUsPersonInfo
                  mismatch={isUsPersonMismatch}
                  name="usPersonInfo"
                />
              ) : null
            }
          </WSForm.Value>

          <WSErrorMessage
            contextKey={ErrorContextKey.MonitoredEntity}
            error={updateMeta.error}
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                label: "Cancel",
                onClick: () => {
                  modalAbandon.open({
                    onContinue: goBack
                  });
                },
                kind: "Secondary",
                type: "button"
              },
              {
                label: "Continue",
                loading: updateMeta.isLoading || loading
              }
            ]}
          />

          <WSText.ParagraphXs color="gray400">
            The information on this page is required to request a taxpayer
            information update, if you exit or leave without completing this
            step, it will cancel the request.
          </WSText.ParagraphXs>
        </WSList>
      </WSForm>
    </WSPanel>
  );
};
